import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Btn from '../Btn/Btn';
import { RewardSettings, TherapyProfile } from '../../services/profileService';
import leavesIcon from '../../assets/images/icon/leaves.png';
import starIcon from '../../assets/images/icon/star.png';
import { getEffectiveProfileRewards } from './profileRewards';

const MAX_REWARD = 2147483647;

const REWARD_KEYS = [
    'questionnaire_energy',
    'expo_energy',
    'nachsorge_energy',
    'questionnaire_stars',
    'expo_stars',
    'nachsorge_stars',
] as const;

type RewardKey = (typeof REWARD_KEYS)[number];
type RewardInputs = Record<RewardKey, string>;

type ProfileEnergySettingsProps = {
    profile: TherapyProfile;
    birthdate?: string;
    isBusy: boolean;
    onSave: (settings: RewardSettings) => Promise<void>;
    onReset: () => Promise<void>;
};

type ParsedReward = {
    value?: number;
    error?: string;
};

type ParsedRewards = Record<RewardKey, ParsedReward>;
type ValidParsedRewards = Record<RewardKey, { value: number; error?: string }>;

const currencySections: Array<{
    id: string;
    title: string;
    icon: string;
    fields: Array<{ key: RewardKey; label: string }>;
}> = [
    {
        id: 'energy',
        title: 'Eucalyptus leaves',
        icon: leavesIcon,
        fields: [
            { key: 'questionnaire_energy', label: 'For each completed questionnaire' },
            { key: 'expo_energy', label: 'For each completed exposition exercise' },
            { key: 'nachsorge_energy', label: 'For each completed aftercare task' },
        ],
    },
    {
        id: 'stars',
        title: 'Stars',
        icon: starIcon,
        fields: [
            { key: 'questionnaire_stars', label: 'For each completed questionnaire' },
            { key: 'expo_stars', label: 'For each completed exposition exercise' },
            { key: 'nachsorge_stars', label: 'For each completed aftercare task' },
        ],
    },
];

function parseReward(value: string, requiredMessage: string, invalidMessage: string): ParsedReward {
    if (value.trim() === '') {
        return { error: requiredMessage };
    }
    if (!/^\d+$/.test(value)) {
        return { error: invalidMessage };
    }
    const parsed = Number(value);
    if (!Number.isSafeInteger(parsed) || parsed < 0 || parsed > MAX_REWARD) {
        return { error: invalidMessage };
    }
    return { value: parsed };
}

function toInputs(settings: RewardSettings): RewardInputs {
    return {
        questionnaire_energy: String(settings.questionnaire_energy),
        expo_energy: String(settings.expo_energy),
        nachsorge_energy: String(settings.nachsorge_energy),
        questionnaire_stars: String(settings.questionnaire_stars),
        expo_stars: String(settings.expo_stars),
        nachsorge_stars: String(settings.nachsorge_stars),
    };
}

function parseRewards(inputs: RewardInputs, requiredMessage: string, invalidMessage: string): ParsedRewards {
    return {
        questionnaire_energy: parseReward(inputs.questionnaire_energy, requiredMessage, invalidMessage),
        expo_energy: parseReward(inputs.expo_energy, requiredMessage, invalidMessage),
        nachsorge_energy: parseReward(inputs.nachsorge_energy, requiredMessage, invalidMessage),
        questionnaire_stars: parseReward(inputs.questionnaire_stars, requiredMessage, invalidMessage),
        expo_stars: parseReward(inputs.expo_stars, requiredMessage, invalidMessage),
        nachsorge_stars: parseReward(inputs.nachsorge_stars, requiredMessage, invalidMessage),
    };
}

function hasParsedValues(parsed: ParsedRewards): parsed is ValidParsedRewards {
    return REWARD_KEYS.every((key) => parsed[key].value !== undefined);
}

function toSettings(parsed: ParsedRewards): RewardSettings | null {
    if (!hasParsedValues(parsed)) {
        return null;
    }
    return {
        questionnaire_energy: parsed.questionnaire_energy.value,
        expo_energy: parsed.expo_energy.value,
        nachsorge_energy: parsed.nachsorge_energy.value,
        questionnaire_stars: parsed.questionnaire_stars.value,
        expo_stars: parsed.expo_stars.value,
        nachsorge_stars: parsed.nachsorge_stars.value,
    };
}

const ProfileEnergySettings = ({
    profile,
    birthdate,
    isBusy,
    onSave,
    onReset,
}: ProfileEnergySettingsProps): JSX.Element => {
    const { t } = useTranslation();
    const effectiveRewards = useMemo(() => getEffectiveProfileRewards(profile, birthdate), [profile, birthdate]);
    const [inputs, setInputs] = useState<RewardInputs>(() => toInputs(effectiveRewards.settings));
    const [pendingAction, setPendingAction] = useState<'save' | 'reset' | null>(null);
    const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    const hasMounted = useRef(false);

    const {
        questionnaire_energy: effectiveQuestionnaireEnergy,
        expo_energy: effectiveExpoEnergy,
        nachsorge_energy: effectiveNachsorgeEnergy,
        questionnaire_stars: effectiveQuestionnaireStars,
        expo_stars: effectiveExpoStars,
        nachsorge_stars: effectiveNachsorgeStars,
    } = effectiveRewards.settings;

    useEffect(() => {
        if (!hasMounted.current) {
            hasMounted.current = true;
            return;
        }
        setInputs({
            questionnaire_energy: String(effectiveQuestionnaireEnergy),
            expo_energy: String(effectiveExpoEnergy),
            nachsorge_energy: String(effectiveNachsorgeEnergy),
            questionnaire_stars: String(effectiveQuestionnaireStars),
            expo_stars: String(effectiveExpoStars),
            nachsorge_stars: String(effectiveNachsorgeStars),
        });
    }, [
        profile.manual_rewards,
        effectiveQuestionnaireEnergy,
        effectiveExpoEnergy,
        effectiveNachsorgeEnergy,
        effectiveQuestionnaireStars,
        effectiveExpoStars,
        effectiveNachsorgeStars,
    ]);

    const requiredMessage = t('Reward value is required.');
    const invalidMessage = t('Rewards must be whole numbers between 0 and 2147483647.');
    const parsed = useMemo(
        () => parseRewards(inputs, requiredMessage, invalidMessage),
        [inputs, requiredMessage, invalidMessage],
    );
    const settings = useMemo(() => toSettings(parsed), [parsed]);
    const isDirty = settings !== null && REWARD_KEYS.some((key) => settings[key] !== effectiveRewards.settings[key]);

    const save = async (): Promise<void> => {
        if (settings === null) {
            setFeedback({ type: 'error', message: invalidMessage });
            return;
        }
        setPendingAction('save');
        setFeedback(null);
        try {
            await onSave(settings);
            setFeedback({ type: 'success', message: t('Reward settings saved.') });
        } catch (error) {
            setFeedback({
                type: 'error',
                message: error instanceof Error ? error.message : t('Failed to save reward settings.'),
            });
        } finally {
            setPendingAction(null);
        }
    };

    const reset = async (): Promise<void> => {
        setPendingAction('reset');
        setFeedback(null);
        try {
            await onReset();
            setFeedback({ type: 'success', message: t('Automatic reward scaling restored.') });
        } catch (error) {
            setFeedback({
                type: 'error',
                message: error instanceof Error ? error.message : t('Failed to reset reward settings.'),
            });
        } finally {
            setPendingAction(null);
        }
    };

    return (
        <section className="frontpage__reward-settings" aria-labelledby="reward-settings-heading">
            <div className="frontpage__reward-title-row">
                <h4 id="reward-settings-heading">{t('Reward settings')}</h4>
                <span
                    className={`frontpage__reward-mode frontpage__reward-mode--${
                        profile.manual_rewards ? 'manual' : 'automatic'
                    }`}
                >
                    {profile.manual_rewards ? t('Manual rewards') : t('Automatic rewards')}
                </span>
            </div>
            <p className="frontpage__reward-note">
                {profile.manual_rewards
                    ? t('These rewards were customized manually.')
                    : effectiveRewards.usesFallbackAge
                    ? t('No valid birthdate is configured. Automatically calculated with fallback age {{age}}.', {
                          age: effectiveRewards.age,
                      })
                    : t('Automatically calculated for age {{age}} from the birthdate.', {
                          age: effectiveRewards.age,
                      })}
            </p>

            <div className="frontpage__reward-currencies">
                {currencySections.map((currency) => (
                    <section
                        className="frontpage__reward-currency"
                        aria-labelledby={`${currency.id}-reward-heading`}
                        key={currency.id}
                    >
                        <div className="frontpage__reward-heading">
                            <img src={currency.icon} alt="" aria-hidden="true" />
                            <h5 id={`${currency.id}-reward-heading`}>{t(currency.title)}</h5>
                        </div>
                        <div className="frontpage__reward-fields">
                            {currency.fields.map((field) => (
                                <label className="frontpage__reward-field" key={field.key}>
                                    <span>{t(field.label)}</span>
                                    <input
                                        type="number"
                                        min={0}
                                        max={MAX_REWARD}
                                        step={1}
                                        required
                                        value={inputs[field.key]}
                                        disabled={isBusy}
                                        aria-label={`${t(currency.title)}: ${t(field.label)}`}
                                        aria-invalid={parsed[field.key].error !== undefined}
                                        onChange={(event) => {
                                            setInputs((current) => ({ ...current, [field.key]: event.target.value }));
                                            setFeedback(null);
                                        }}
                                    />
                                    {parsed[field.key].error && (
                                        <span className="frontpage__reward-validation">{parsed[field.key].error}</span>
                                    )}
                                </label>
                            ))}
                        </div>
                    </section>
                ))}
            </div>

            {feedback && (
                <div
                    className={feedback.type === 'success' ? 'frontpage__success' : 'frontpage__error'}
                    role={feedback.type === 'error' ? 'alert' : 'status'}
                >
                    {feedback.message}
                </div>
            )}
            <div className="frontpage__profile-actions">
                {profile.manual_rewards && (
                    <Btn
                        title={
                            pendingAction === 'reset' && isBusy ? t('Resetting...') : t('Reset to automatic rewards')
                        }
                        variant="secondary"
                        onClick={reset}
                        disabled={isBusy}
                    />
                )}
                <Btn
                    title={pendingAction === 'save' && isBusy ? t('Saving...') : t('Save reward settings')}
                    variant="primary"
                    onClick={save}
                    disabled={isBusy || !isDirty || settings === null}
                />
            </div>
        </section>
    );
};

export default ProfileEnergySettings;
